// Vendors
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Controller, FieldError, useFormContext } from 'react-hook-form'

// Functions
import { useSelectParser } from 'hooks/useSelectParser'

// Components
import { FieldMask } from 'components/FieldMask'
import { FieldText } from 'components/FieldText'
import { FieldDate } from 'components/FieldDate'
import { FieldSelect } from 'components/FieldSelect'
import { FieldRadioButton } from 'components/FieldRadioButton'

// Styles
import { Grid, GridItem } from '@chakra-ui/react'

// Types
import { Company } from 'types/Company'
import { SellerNewForm } from '../types'

export const TabPersonalData = () => {
  /*
  |-----------------------------------------------------------------------------
  | Constants
  |-----------------------------------------------------------------------------
  |
  |
  */

  const { t } = useTranslation('seller')

  const { options: optionsTestfyBase, isLoading: isLoadingTestfyBase } =
    useSelectParser<Company>({
      accessor: { value: 'id', label: 'internalIdentification' },
      endpoint: '/app/company?type=testfy-base',
    })

  const {
    control,
    register,
    formState: { errors },
  } = useFormContext<SellerNewForm>()

  /*
  |-----------------------------------------------------------------------------
  | Memos
  |-----------------------------------------------------------------------------
  |
  |
  */

  const genresOptions = useMemo(
    () => [
      {
        label: 'Masculino',
        value: 'M',
      },
      {
        label: 'Feminino',
        value: 'F',
      },
    ],
    []
  )

  /*
  |-----------------------------------------------------------------------------
  | Renders
  |-----------------------------------------------------------------------------
  |
  |
  */

  return (
    <Grid
      my="4"
      gap="10"
      templateColumns={{ lg: 'repeat(2, 1fr)', xl: 'repeat(4, 1fr)' }}
    >
      <GridItem colSpan={2}>
        <FieldText
          label={t('form.name.label')}
          error={errors.name}
          isRequired
          {...register('name')}
        />
      </GridItem>

      <GridItem colSpan={2}>
        <FieldText
          canModify={false}
          label={t('form.email.label')}
          error={errors.email}
          isRequired
          {...register('email')}
        />
      </GridItem>

      <GridItem colSpan={2}>
        <FieldMask
          label={t('form.phoneNumber.label')}
          mask="(99) 9 9999-9999"
          error={errors.phoneNumber}
          isRequired
          {...register('phoneNumber')}
        />
      </GridItem>

      <GridItem colSpan={2}>
        <FieldMask
          label={t('form.documentNumber.label')}
          mask="999.999.999-99"
          error={errors.documentNumber}
          isRequired
          {...register('documentNumber')}
        />
      </GridItem>

      <GridItem colSpan={2}>
        <Controller
          name="birthdate"
          control={control}
          render={({ field }) => (
            <FieldDate
              label={t('form.birthdate.label')}
              name={field.name}
              selected={field.value}
              onChange={field.onChange}
              error={errors.birthdate}
              maxDate={new Date()}
              isRequired
            />
          )}
        />
      </GridItem>

      <GridItem colSpan={2}>
        <FieldRadioButton
          label={t('form.gender.label')}
          name="gender"
          control={control}
          radioButtonDirection="row"
          options={genresOptions}
          isRequired
        />
      </GridItem>

      <GridItem colSpan={2}>
        <Controller
          name="companyId"
          control={control}
          render={({ field }) => (
            <FieldSelect
              label={t('form.companyId.label')}
              error={errors.companyId as FieldError}
              isLoading={isLoadingTestfyBase}
              options={optionsTestfyBase}
              isRequired
              {...field}
            />
          )}
        />
      </GridItem>
    </Grid>
  )
}
